import { Data, Model, Utils } from '@singularsystems/neo-core';
import { AxiosPromise } from 'axios';
import { injectable } from 'inversify';
import { AppService, Types } from '../../App/Services/AppService';
import CommandResult from '../../Common/Models/CommandResult';
import UpdateTradeOrderCommand from '../TradeOrders/Models/Commands/UpdateTradeOrderCommand';
import ReleaseTradeOrdersCommand from '../TradeOrders/Models/Commands/ReleaseTradeOrdersCommand';
import TradeOrderLookup from '../TradeOrders/Models/Lookups/TradeOrderLookup';
import TradeOrderSearchCriteria from '../TradeOrders/Models/Queries/TradeOrderSearchCriteria';

export interface ITradeOrderApiClient {
    /**
     * Get trade orders paged list.
     * @param request Page request.
     * @returns Page result of trade order lookups.
     */
    getTradeOrdersPagedList(request: Model.PartialPlainNonTrackedObject<Data.PageRequest<TradeOrderSearchCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<TradeOrderLookup>>>;

    /**
     * Update trade order.
     * @param command Update trade order command.
     * @returns A Task representing the asynchronous operation.
     */
    updateTradeOrder(command: Model.PartialPlainObject<UpdateTradeOrderCommand>): AxiosPromise<Model.PlainTrackedObject<CommandResult>>;

    /**
     * Get a single trade order by its ID.
     * @param id TradeOrderId.
     * @returns The trade order if found, otherwise NotFound.
     */
    getTradeOrderById(id: number): AxiosPromise<Model.PlainTrackedObject<TradeOrderLookup>>;

    /**
     * Initiates processing of trade orders that are marked as released.
     */
    processReleasedTradeOrders(): AxiosPromise<Model.PlainTrackedObject<CommandResult>>;

    /**
     * Batch release trade orders.
     * @param command Release trade orders command.
     * @returns A Task representing the asynchronous operation.
     */
    releaseTradeOrders(command: Model.PartialPlainObject<ReleaseTradeOrdersCommand>): AxiosPromise<Model.PlainTrackedObject<CommandResult>>;

    /**
 * Upload attachment file for a trade order.
     * @param tradeOrderId Trade order id.
     * @param file File to upload.
     * @returns Upload result.
     */
    uploadTradeOrderAttachment(tradeOrderId: number, file: File): AxiosPromise<Model.PlainTrackedObject<CommandResult>>;

    /**
     * Download attachment file for a trade order.
     * @param tradeOrderId Trade order id.
     * @returns File blob uri.
     */
    downloadTradeOrderAttachment(tradeOrderId: number): AxiosPromise<string>;

    // Client only properties / methods
}
@injectable()
export default class TradeOrderApiClient extends Data.ApiClientBase implements ITradeOrderApiClient {

    constructor (config = AppService.get(Types.Config)) {
        super(`${config.apiPath}/TradeOrder`);
    }

    public getTradeOrdersPagedList(request: Model.PartialPlainNonTrackedObject<Data.PageRequest<TradeOrderSearchCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<TradeOrderLookup>>> {
        return this.axios.get(`${this.apiPath}/GetTradeOrdersPagedList?${Utils.getQueryString(request)}`);
    }

    public updateTradeOrder(command: Model.PartialPlainObject<UpdateTradeOrderCommand>): AxiosPromise<Model.PlainTrackedObject<CommandResult>> {
        return this.axios.post(`${this.apiPath}/UpdateTradeOrder`, command);
    }

    public getTradeOrderById(id: number): AxiosPromise<Model.PlainTrackedObject<TradeOrderLookup>> {
        return this.axios.get(`${this.apiPath}/GetTradeOrderById/${id}`);
    }

    public processReleasedTradeOrders(): AxiosPromise<Model.PlainTrackedObject<CommandResult>> {
        return this.axios.post(`${this.apiPath}/ProcessReleasedTradeOrders`);
    }

    public exportReleasedTradeOrders(): AxiosPromise<Model.PlainTrackedObject<CommandResult>> {
        return this.axios.post(`${this.apiPath}/ExportTradeOrderReleaseFiles`);
    }

    /**
     * Batch release trade orders.
     * @param command Release trade orders command with list of IDs.
     * @returns Command result with success/failure details.
     */
    public releaseTradeOrders(command: Model.PartialPlainObject<ReleaseTradeOrdersCommand>): AxiosPromise<Model.PlainTrackedObject<CommandResult>> {
        return this.axios.post(`${this.apiPath}/ReleaseTradeOrders`, command);
    }

    public uploadTradeOrderAttachment(
        tradeOrderId: number,
        file: File
    ): AxiosPromise<Model.PlainTrackedObject<CommandResult>> {
        const bodyFormData = new FormData();
        bodyFormData.set("file", file, file.name);
        bodyFormData.set("files", file, file.name);
        bodyFormData.set("tradeOrderId", tradeOrderId.toString());

        return this.axios.post(`${this.apiPath}/UploadTradeOrderAttachment`, bodyFormData, {
            headers: { "Content-Type": "multipart/form-data" }
        });
    }

    public downloadTradeOrderAttachment(tradeOrderId: number): AxiosPromise<string> {
        return this.axios.get(`${this.apiPath}/DownloadTradeOrderAttachment/${tradeOrderId}`);
    }

    public downloadTradeOrder(selectedTradeOrderIDs: number[]): void {
        alert(`TradeOrderIds for download are: ${selectedTradeOrderIDs.join(", ")}`);
        // Actual API call can be added here later if needed
    }
}